// What a supplier actually has to DO with a compliance document.
//
// Documents are satisfied four different ways (fusion_documents.satisfied_by):
//
//   upload      → send us a file (insurance certificate, accreditation)
//   acknowledge → read it and sign/confirm (Code of Conduct, policies)
//   form        → fill in a hosted form and sign it
//   pdf_sign    → sign fields placed on a PDF we supply
//
// Only the first involves uploading anything. The supplier portal already
// words its buttons per method ("Sign PDF", "Complete form"), but the chase
// email said "not yet uploaded" for every outstanding document — so a supplier
// chased about the Fusion Code of Conduct was told to upload a document that
// only ever needed signing, and had nothing to upload.
//
// One helper, so the email, the queue and any future surface can't drift apart
// on what they're asking for.

// Anything not recognised is treated as an upload — that's the historic
// default in fusion_documents and the safest fallback: "upload" for a
// signable doc is wrong but recoverable; the alternative is a blank action.
const UPLOAD = 'upload';

// Imperative — "Please <verb> the following".
const VERBS = {
  upload: 'upload',
  acknowledge: 'sign',
  form: 'complete',
  pdf_sign: 'sign',
};

// State — "<Document>: <label>". Past-participle phrasing for a thing that
// hasn't happened yet.
const OUTSTANDING = {
  upload: 'not yet uploaded',
  acknowledge: 'not yet signed',
  form: 'not yet completed',
  pdf_sign: 'not yet signed',
};

// What a REJECTED document needs next. An upload gets replaced with a new
// file; a signed one has to be signed again — there is no file to swap.
const REJECTED = {
  upload: 'not accepted — needs replacing',
  acknowledge: 'not accepted — needs signing again',
  form: 'not accepted — needs completing again',
  pdf_sign: 'not accepted — needs signing again',
};

// Sentence-leading form for the admin queue ("Never uploaded · required
// since …"). Same distinction as OUTSTANDING, different grammar.
const NEVER = {
  upload: 'Never uploaded',
  acknowledge: 'Never signed',
  form: 'Never completed',
  pdf_sign: 'Never signed',
};

// An EXPIRED document needs the same action as a never-done one, so this
// reuses the verb rather than keeping a fourth table in step.
const norm = (satisfiedBy) => (VERBS[satisfiedBy] ? satisfiedBy : UPLOAD);

export const actionVerb = (satisfiedBy) => VERBS[norm(satisfiedBy)];
export const outstandingLabel = (satisfiedBy) => OUTSTANDING[norm(satisfiedBy)];
export const rejectedLabel = (satisfiedBy) => REJECTED[norm(satisfiedBy)];
export const neverDoneLabel = (satisfiedBy) => NEVER[norm(satisfiedBy)];

// Expired documents keep the expiry date in the label where we have one —
// that date is the whole reason it's being chased.
export const expiredLabel = (satisfiedBy, formattedExpiryDate) => {
  const needs = actionVerb(satisfiedBy) === 'upload' ? 'a new copy' : 'signing again';
  return formattedExpiryDate
    ? `expired ${formattedExpiryDate} — needs ${needs}`
    : `expired — needs ${needs}`;
};

// One line covering a whole chase list. When every outstanding document is
// satisfied the same way we can be specific ("Please sign the following");
// a mixed list has to stay generic, because telling someone to sign an
// insurance certificate is exactly the bug this module exists to prevent.
export const chaseInstruction = (satisfiedByList = []) => {
  const verbs = new Set((satisfiedByList.length ? satisfiedByList : [UPLOAD]).map(actionVerb));
  if (verbs.size === 1) {
    const only = verbs.values().next().value;
    if (only === 'upload') return 'Please upload the following:';
    if (only === 'sign') return 'Please sign the following:';
    if (only === 'complete') return 'Please complete the following:';
  }
  return 'Please action the following — each one shows what it needs:';
};
